import { ColorString, MonitorStatus } from '@fe/core';
import {
    AirportTooltipInfoField,
    AlertSoundType,
    ApiEnvironments,
    DateDelimitersEnum,
    DateTimeFormatEnum,
    DistanceUnitsEnum,
    FlightFobVolumeDisplayUnit,
    FlightFobWeightDisplayUnit,
    FlightListFilterMode,
    LatLongDisplayFormatEnum,
    LatLongDisplayFormatLogFileEnum,
    MapGotoTarget,
    MapLanguageMode,
    OnMapControlsDisplayEnum,
    StarmapType,
    TimeDisplayFormat,
    TimeDisplayZone,
    WeatherLegendDateTimeDisplay
} from '../common/enums';
import { ListState } from '../components/common/types';
import {
    DiversionManagerAirlineEnum,
    DiversionManagerCapacityFieldEnum,
    DiversionManagerNetworkEnum,
    DiversionManagerOtherEnum,
    DiversionPriorityTypeEnum
} from '../components/diversion/types';
import { MyFleetAcidListItem } from '../components/my-fleet/types';
import { AirportTooltipFieldSelection } from '../static/static-airport-tooltip-fields';
import { SettingsWorldClockDateFormat, SettingsWorldClocks, SettingsWorldClockTimeFormat } from '../types/clocks';
import {
    AircraftTypeRecord,
    AirportIdDisplayStyleEnum,
    AirportMonitorSortType,
    BaseMapInfo,
    CustomEmailAlert,
    DashboardWidgetSettings,
    DEFAULT_WELCOME_HELPER,
    EliCustomButton,
    EliDisplayModeEnum,
    EventListBackgroundColorMode,
    FEWebSettingModel,
    FleetMonitorSortType,
    MapAutoScreenshotTarget,
    MapImageFormat,
    MapTooltipSettings,
    MapUpdateMode,
    NavMapInfo,
    SortOrderEnum,
    WelcomeHelperSettings,
    WorkspaceLayout
} from '../types/settings';
import { WorkspaceSettings } from '../types/workspaces';
import {
    AlwaysLoadWeatherItem,
    MyFleetAcidListSource,
    MyFleetAirportModeEnum,
    MyFleetMode,
    UdViewModel,
    UDViewTagSelectionEnum
} from '../udView/udview';
import { AircraftEdrSettings } from './actor-messages';
import { SavedDashboardLayout } from '../app/features/dashboard/dashboard-custom-layouts';

export enum SyncType {
    cloud,
    local
}

interface SettingsDefinition<T = unknown> {
    readonly key: string;
    readonly default: T;
    // This field is used to compare serialized user settings with default values
    // Regular equality is not enough because empty object or array are not equal to each other ({} === {),[] === [], are both false)
    readonly defaultValueJson: string;
    readonly sync: SyncType;
}

const defaultEdrSettings: AircraftEdrSettings = {
    light: {
        moderate: 0.16,
        severe: 0.36,
        extreme: 0.64
    },
    medium: {
        moderate: 0.2,
        severe: 0.44,
        extreme: 0.79
    },
    heavy: {
        moderate: 0.24,
        severe: 0.54,
        extreme: 0.96
    },
    custom: []
} as const;

const defaultWorkspaceSettings: WorkspaceSettings = {
    layout: WorkspaceLayout.single,
    views: [],
    panels: {
        airportMonitor: false,
        fleetMonitor: false,
        eventList: false,
        legend: false,
        routeAnalyzer: false
    },
    windows: []
} as const;

const defaultWorldClockSettings: SettingsWorldClocks = {
    enabled: false,
    clocks: [],
    barColor: 'rgb(0,0,0)',
    timeColor: 'rgb(255,0,0)',
    dateColor: 'rgb(200,0,0)',
    nameColor: 'rgb(175,175,0)',
    addOffsetToName: false,
    timeZoneUppercase: false,
    dateFormat: SettingsWorldClockDateFormat.dayOfWeekMonthDay,
    timeFormat: SettingsWorldClockTimeFormat.h24
} as const;

// List of all the settings
// prettier-ignore
export const SETTINGS_DEFINITIONS = {
    airportIdDisplayStyle: createSettings<AirportIdDisplayStyleEnum>(
        'AirportIdDisplayStyle',
        AirportIdDisplayStyleEnum.iata,
        SyncType.cloud
    ),
    fleetMonitorShow5thDataRow: createSettings<boolean>('FleetMonitorShow5thDataRow', false, SyncType.cloud),
    fleetMonitorShowScheduledFlights: createSettings<boolean>('FleetMonitorShowScheduledFlights', false, SyncType.cloud),
    fleetMonitorShowPlannedFlights: createSettings<boolean>('FleetMonitorShowPlannedFlights', false, SyncType.cloud),
    fleetMonitorCollapsedView: createSettings<boolean>('FleetMonitorCollapsedView', false, SyncType.cloud),
    showAircraftMonitorStatus: createSettings<boolean>('ShowAircraftMonitorStatus', false, SyncType.cloud),
    showAirportMonitorStatus: createSettings<boolean>('ShowAirportMonitorStatus', false, SyncType.cloud),
    showWatchedAircraftIcons: createSettings<boolean>('ShowWatchedAircraftIcons', false, SyncType.cloud),
    OnMapControlsDisplay: createSettings<OnMapControlsDisplayEnum>('OnMapControlsDisplay', OnMapControlsDisplayEnum.Show, SyncType.cloud),
    flightFobWeightDisplayUnit: createSettings<FlightFobWeightDisplayUnit>(
        'FlightFobWeightDisplayUnit',
        FlightFobWeightDisplayUnit.Pound,
        SyncType.cloud
    ),
    flightFobVolumeDisplayUnit: createSettings<FlightFobVolumeDisplayUnit>(
        'FlightFobVolumeDisplayUnit',
        FlightFobVolumeDisplayUnit.Gallon,
        SyncType.cloud
    ),
    defaultFlightMonitorSortType: createSettings<FleetMonitorSortType>(
        'DefaultFlightMonitorSortType',
        FleetMonitorSortType.monitorStatusPriorityCallsignFlightStatus,
        SyncType.cloud
    ),
    fleetMonitorFlightStatusColorBackground: createSettings<boolean>('FleetMonitorFlightStatusColorBackground', false, SyncType.cloud),
    fleetMonitorSortOrder: createSettings<SortOrderEnum>('FleetMonitorSortOrder', SortOrderEnum.asc, SyncType.cloud),
    fleetMonitorMinimumLevel: createSettings<MonitorStatus>('FleetMonitorMinimumLevel', MonitorStatus.null, SyncType.cloud),
    fleetMonitorAirportSplitView: createSettings<boolean>('FleetMonitorAirportSplitView', true, SyncType.cloud),
    defaultAirportSortType: createSettings<AirportMonitorSortType>(
        'DefaultAirportSortType',
        AirportMonitorSortType.monitorStatusPriorityId,
        SyncType.cloud
    ),
    airportMonitorMonitorSortOrder: createSettings<SortOrderEnum>('AirportMonitorMonitorSortOrder', SortOrderEnum.asc, SyncType.cloud),
    airportMonitorMinimumLevel: createSettings<MonitorStatus>('AirportMonitorMinimumLevel', MonitorStatus.null, SyncType.cloud),
    airportMonitorCollapsedView: createSettings<boolean>('AirportMonitorCollapsedView', false, SyncType.cloud),
    timeDisplayZone: createSettings<TimeDisplayZone>('TimeDisplayZone', TimeDisplayZone.Utc, SyncType.cloud),
    timeDisplayFormat: createSettings<TimeDisplayFormat>('TimeDisplayFormat', TimeDisplayFormat.H24, SyncType.cloud),
    dateDisplayFormat: createSettings<DateTimeFormatEnum>('DateDisplayFormat', DateTimeFormatEnum.DayMonthYear, SyncType.cloud),
    dateDelimiter: createSettings<DateDelimitersEnum>('DateDelimiter', DateDelimitersEnum.Slash, SyncType.cloud),
    distanceUnit: createSettings<DistanceUnitsEnum>('DistanceUnit', DistanceUnitsEnum.NauticalMiles, SyncType.cloud),
    latLongDisplayFormat: createSettings<LatLongDisplayFormatEnum>(
        'LatLongDisplayFormat',
        LatLongDisplayFormatEnum.DegreesAndDecimalMinutes,
        SyncType.cloud
    ),
    showDayWithTimes: createSettings<boolean>('ShowDayWithTimes', true, SyncType.cloud),
    shouldDisconnectAfterInactivity: createSettings<boolean>('ShouldDisconnectAfterInactivity', false, SyncType.cloud),
    disconnectAfterMinutes: createSettings<number>('DisconnectAfterMinutes', 30, SyncType.cloud),
    weatherLegendDateTimeDisplayStyle: createSettings<WeatherLegendDateTimeDisplay>(
        'WeatherLegendDateTimeDisplayStyle',
        WeatherLegendDateTimeDisplay.timeDate,
        SyncType.cloud
    ),
    weatherLegendDisplayModelRunTime: createSettings<boolean>('WeatherLegendDisplayModelRunTime', true, SyncType.cloud),
    weatherLegendDisplayVendorId: createSettings<boolean>('WeatherLegendDisplayVendorId', true, SyncType.cloud),
    alwaysLoadWeather: createSettings<AlwaysLoadWeatherItem[]>('AlwaysLoadWeather', [], SyncType.cloud),
    queryToSaveViewIfChanged: createSettings<boolean>('QueryToSaveViewIfChanged', true, SyncType.cloud),
    timeToShowEachViewSeconds: createSettings<number>('TimeToShowEachViewSeconds', 15, SyncType.cloud),
    isDark: createSettings<boolean>('IsDark', true, SyncType.cloud),
    myFleetMode: createSettings<MyFleetMode>('MyFleetMode', MyFleetMode.disabled, SyncType.cloud),
    myFleetAcidListSource: createSettings<MyFleetAcidListSource>('MyFleetAcidListSource', MyFleetAcidListSource.manual, SyncType.cloud),
    myFleetAirlines: createSettings<string[]>('MyFleetAirlines', [], SyncType.cloud),
    myFleetAirlinesApplyFlightNumberPattern: createSettings<boolean>('myFleetAirlinesApplyFlightNumberPattern', false, SyncType.cloud),
    myFleetAirport: createSettings<string | null>('MyFleetAirport', null, SyncType.cloud),
    myFleetAirportMode: createSettings<MyFleetAirportModeEnum>(
        'MyFleetAirportMode',
        MyFleetAirportModeEnum.departureArrivals,
        SyncType.cloud
    ),
    myFleetAcidList: createSettings<MyFleetAcidListItem[]>('myFleetAcidList', [], SyncType.cloud),
    myFleetDeskFilter: createSettings<string[]>('DeskFilterArray', [], SyncType.cloud),
    myAirportFilterGroup: createSettings<string | null>('MyAirportFilterGroup', null, SyncType.cloud),
    myAirportsIncludeScheduledMyFleet: createSettings('myAirportsIncludeScheduledMyFleet', false, SyncType.cloud),
    myAirportIncludeMyFleetOrgDestAlt: createSettings<boolean>('MyAirportIncludeMyFleetOrgDestAlt', true, SyncType.cloud),
    favoriteWeather: createSettings<number[]>('FavoriteWeather', [], SyncType.cloud),
    leftNavOpen: createSettings<boolean>('LeftNavOpen', false, SyncType.cloud),
    startInFullscreenMode: createSettings<boolean>('StartInFullscreenMode', false, SyncType.cloud),
    autoStartSlideshow: createSettings<boolean>('AutoStartSlideshow', false, SyncType.cloud),
    autoStartRecording: createSettings<boolean>('AutoStartRecording', false, SyncType.cloud),
    splitRecordingAtMidnight: createSettings<boolean>('SplitRecordingAtMidnight', false, SyncType.cloud),
    /** @deprecated removed */
    autoDownloadRecordingAtMidnight: createSettings<boolean>('AutoDownloadRecordingAtMidnight', false, SyncType.cloud),
    drawRouteFlown: createSettings<boolean>('DrawRouteFlown', true, SyncType.cloud),
    drawDirectRouteWhenNoRouteAvailable: createSettings<boolean>('drawDirectRouteWhenNoRouteAvailable', false, SyncType.cloud),
    routeFlownPoints: createSettings<boolean>('RouteFlownPoints', true, SyncType.cloud),
    routeFlownLabels: createSettings<boolean>('RouteFlownLabels', false, SyncType.cloud),
    colorizeRouteFlown: createSettings<boolean>('ColorizeRouteFlown', true, SyncType.cloud),
    smoothenRouteFlown: createSettings<boolean>('SmoothenRouteFlown', true, SyncType.cloud),
    enablePlannedRoutes: createSettings<boolean>('EnablePlannedRoutes', true, SyncType.cloud),
    plannedRoutePoints: createSettings<boolean>('PlannedRoutePoints', true, SyncType.cloud),
    plannedRouteLabels: createSettings<boolean>('PlannedRouteLabels', true, SyncType.cloud),
    plannedRouteTimeDistance: createSettings<boolean>('PlannedRouteTimeDistance', true, SyncType.cloud),
    drawPlannedRoute: createSettings<boolean>('DrawPlannedRoute', false, SyncType.cloud), // ETOPS
    showEtops: createSettings<boolean>('ShowETOPS', true, SyncType.cloud),
    etopsBorderFillColor: createSettings<ColorString>('ETOPSBorderFillColor', 'rgba(0, 75, 148, 0.08)', SyncType.cloud),
    etopsBorderOutlineColor: createSettings<ColorString>('ETOPSBorderOutlineColor', 'rgba(0, 75, 148, .39)', SyncType.cloud),
    etopsRuleFillColor: createSettings<ColorString>('ETOPSRuleFillColor', 'rgba(128, 128, 128, 0.05)', SyncType.cloud),
    etopsRuleOutlineColor: createSettings<ColorString>('ETOPSRuleOutlineColor', 'rgba(128, 128, 128, .39)', SyncType.cloud),
    etopsPointColor: createSettings<ColorString>('ETOPSPointColor', 'rgba(50, 50, 255, .69)', SyncType.cloud),
    etopsAirportColor: createSettings<ColorString>('ETOPSAptColor', '#000', SyncType.cloud),
    mapGotoTargetMode: createSettings<MapGotoTarget>('MapGotoTargetMode', MapGotoTarget.active, SyncType.cloud),
    mapFlashMarkerDurationSeconds: createSettings<number>('MapFlashMarkerDurationSeconds', 10, SyncType.cloud),
    drawAtcRoute: createSettings<boolean>('DrawAtcRoute', true, SyncType.cloud),
    showHighlightedTag: createSettings<boolean>('ShowHighlightedTag', true, SyncType.cloud),
    enableDebugFeatures: createSettings<boolean>('EnableDebugFeatures', true, SyncType.cloud),
    tooltipFields: createSettings<UDViewTagSelectionEnum[]>(
        'TooltipFields',
        [
            UDViewTagSelectionEnum.Altitude,
            UDViewTagSelectionEnum.Speed,
            UDViewTagSelectionEnum.TailNumber,
            UDViewTagSelectionEnum.AircraftType
        ],
        SyncType.cloud
    ),
    lastSystemHubUpdate: createSettings<string | null>('LastSystemHubUpdate', null, SyncType.cloud),
    lastRunWhatsNewVersion: createSettings<string | null>('LastRunWhatsNewVersion', null, SyncType.cloud),
    flightListFilterMode: createSettings<FlightListFilterMode>('FlightListFilterMode', FlightListFilterMode.all, SyncType.cloud),
    alertEmailSubject: createSettings<string | null>('AlertEmailSubject', null, SyncType.cloud),
    alertEmailToAddress: createSettings<string | null>('AlertEmailToAddress', null, SyncType.cloud),
    alertCustomEmailAlerts: createSettings<CustomEmailAlert[]>('alertCustomEmailAlerts', [], SyncType.cloud),
    focusEnabled: createSettings<boolean>('FocusEnabled', false, SyncType.cloud),
    profileEnabled: createSettings<boolean>('ProfileEnabled', false, SyncType.cloud),
    encompassEnabled: createSettings<boolean>('EncompassEnabled', false, SyncType.cloud),
    addSpaceAfterCommaLogFile: createSettings<boolean>('AddSpaceAfterCommaLogFile', false, SyncType.cloud),
    addHeadingLogFile: createSettings<boolean>('AddHeadingLogFile', false, SyncType.cloud),
    constraintsShowOnMap: createSettings<boolean>('ConstraintsShowOnMap', false, SyncType.cloud),
    diversionShowOnMap: createSettings<boolean>('DiversionShowOnMap', false, SyncType.cloud),
    timeZoneLogFile: createSettings<TimeDisplayZone>('TimeZoneLogFile', TimeDisplayZone.Utc, SyncType.cloud),
    timeFormatLogFile: createSettings<TimeDisplayFormat>('TimeFormatLogFile', TimeDisplayFormat.H24, SyncType.cloud),
    sanitizeTailNumbersLogFile: createSettings<boolean>('SanitizeTailNumbersLogFile', true, SyncType.cloud),
    includeScratchPadLogFile: createSettings<boolean>('IncludeScratchPadLogFile', false, SyncType.cloud),
    positionFormatLogFile: createSettings<LatLongDisplayFormatLogFileEnum>(
        'PositionFormatLogFile',
        LatLongDisplayFormatLogFileEnum.Default,
        SyncType.cloud
    ),
    mapImageFormat: createSettings<MapImageFormat>('MapImageFormat', MapImageFormat.png, SyncType.cloud),
    mapImageFileName: createSettings<string>('MapImageFileName', 'FeMap', SyncType.cloud),
    mapImageAutoSlideShow: createSettings<boolean>('mapImageAutoSlideShow', false, SyncType.cloud),
    mapImageAutoSlideShowTarget: createSettings<MapAutoScreenshotTarget>('mapImageAutoSlideShowTarget', MapAutoScreenshotTarget.all, SyncType.cloud),
    appendDateToMapImageFileName: createSettings<boolean>('AppendDateToMapImageFileName', false, SyncType.cloud),
    mapImageServerUrl: createSettings<string | null>('MapImageServerUrl', null, SyncType.cloud),
    mapImageWidth: createSettings<number | null>('MapImageWidth', null, SyncType.cloud),
    mapImageHeight: createSettings<number | null>('MapImageHeight', null, SyncType.cloud),
    eliDisplayMode: createSettings<EliDisplayModeEnum>('EliDisplayMode', EliDisplayModeEnum.newTab, SyncType.cloud),
    eliAirplaneButtons: createSettings<EliCustomButton[]>('EliAirplaneButtons', [], SyncType.cloud),
    eliAirportButtons: createSettings<EliCustomButton[]>('EliAirportButtons', [], SyncType.cloud),
    eliTopBarButtons: createSettings<EliCustomButton[]>('EliTopBarButtons', [], SyncType.cloud),
    alertSoundType: createSettings<AlertSoundType>('AlertSoundType', AlertSoundType.sound1, SyncType.cloud),
    alertSoundVolume: createSettings<number>('AlertSoundVolume', 50, SyncType.cloud),
    hasStars: createSettings<boolean>('HasStars', true, SyncType.cloud),
    starmapType: createSettings<StarmapType>('StarmapType', StarmapType.basic, SyncType.cloud),
    hasAtmosphere: createSettings<boolean>('HasAtmosphere', true, SyncType.cloud),
    navMapUpdateMode: createSettings<MapUpdateMode>('NavMapUpdateMode', MapUpdateMode.automatic, SyncType.cloud),
    navMap: createSettings<NavMapInfo | null>('NavMap', null, SyncType.cloud),
    aircraftParameters: createSettings<AircraftTypeRecord[]>('AircarftParameters', [], SyncType.cloud),
    defaultAircraftParameters: createSettings<AircraftTypeRecord | null>(
        'DefaultAircraftParameters',
        { speed: 389, fuelBurningRate: null, fuelReserve: null, acType: null } satisfies AircraftTypeRecord,
        SyncType.cloud
    ),
    aircraftEdrSettings: createSettings<AircraftEdrSettings>('AircraftEdrSettings', defaultEdrSettings, SyncType.cloud),
    flightListState: createSettings<ListState | null>('flightListState', null, SyncType.cloud),
    flightListOrder: createSettings<string[] | null>('flightListOrder', null, SyncType.cloud),
    airportListState: createSettings<ListState | null>('airportListState', null, SyncType.cloud),
    airportListOrder: createSettings<string[] | null>('airportListOrder', null, SyncType.cloud),
    dashboardWidgets: createSettings<DashboardWidgetSettings[] | null>('dashboardWidgets', null, SyncType.cloud),
    dashboardCustomLayouts: createSettings<SavedDashboardLayout[] | null>('dashboardCustomLayouts', null, SyncType.cloud),
    favoriteAddRouteMethod: createSettings<number>('favoriteAddRouteMethod', 0, SyncType.cloud),
    graphicRouteEditInfoPopupDismissed: createSettings<boolean>('graphicRouteEditInfoPopupDismissed', false, SyncType.cloud),
    commandCenterHintDismissed: createSettings<boolean>('CommandCenterHintDismissed', false, SyncType.cloud),
    workspaceConfig: createSettings<WorkspaceSettings>('workspaceConfig', defaultWorkspaceSettings, SyncType.cloud),
    mapLanguage: createSettings<MapLanguageMode>('mapLanguage', MapLanguageMode.english, SyncType.cloud),
    tooltipSettings: createSettings<MapTooltipSettings>('tooltip', { requireCtrlKey: false, items: { planes: true, weather: true, navData: true, other: true }}, SyncType.cloud),
    airportTooltipFields: createSettings<AirportTooltipFieldSelection[]>(
        'AirportTooltipFields',
        [
            AirportTooltipInfoField.AptType,
            AirportTooltipInfoField.MagVar,
            AirportTooltipInfoField.Elevation,
            AirportTooltipInfoField.Use,
            AirportTooltipInfoField.ControlTower,
            AirportTooltipInfoField.IcaoArff,
            AirportTooltipInfoField.FaaArff
        ],
        SyncType.cloud
    ),
    worldClocks: createSettings<SettingsWorldClocks>('worldClocks', defaultWorldClockSettings, SyncType.cloud),
    hideNonBreachedThresholdsFlightContextMenu: createSettings('hideNonBreachedThresholdsFlightContextMenu', false, SyncType.cloud),
    hideNonBreachedThresholdsAirportContextMenu: createSettings('hideNonBreachedThresholdsAirportContextMenu', false, SyncType.cloud),
    toolbarShowConstraints: createSettings('toolbarShowConstraints', true, SyncType.cloud),
    toolbarShowMonitor: createSettings('toolbarShowMonitor', true, SyncType.cloud),
    eventListDisplayMonitorStatus: createSettings('eventListDisplayMonitorStatus', false, SyncType.cloud),
    eventListDisplayImportantIndicator: createSettings('eventListDisplayImportantIndicator', true, SyncType.cloud),
    eventListBackgroundColorMode: createSettings('eventListBackgroundColorMode', EventListBackgroundColorMode.default, SyncType.cloud),
    eventListExpandedView: createSettings('eventListExpandedView', false, SyncType.cloud),
    eventListDisplayFlightStatus: createSettings('eventListDisplayFlightStatus', true, SyncType.cloud),

    // Local settings
    rememberMe: createSettings<boolean>('RememberMeKey', false, SyncType.local),
    rememberIntegrationId: createSettings<boolean>('RememberIntegrationIdKey', false, SyncType.local),
    integrationId: createSettings<string | null>('IntegrationIdKey', null, SyncType.local),
    baseMap: createSettings<BaseMapInfo | null>('BaseMapKey', null, SyncType.local),
    overrideApiEnvironment: createSettings<ApiEnvironments | null>('OverrideApiEnvironmentKey', null, SyncType.local),
    overrideWebsocketUrl: createSettings<string | null>('OverrideWebsocketUrlKey', null, SyncType.local),
    deviceId: createSettings<string | null>('DeviceIdKey', null, SyncType.local),
    udViews: createSettings<UdViewModel[]>('UdViewsKey', [], SyncType.local),
    lastUdViewSyncDate: createSettings<string | null>('LastUdViewSyncDateKey', null, SyncType.local),
    settingsCache: createSettings<FEWebSettingModel | null>('SettingsCacheKey', null, SyncType.local),
    lastSettingsSyncDate: createSettings<string | null>('LastSettingsSyncDateKey', null, SyncType.local),
    username: createSettings<string | null>('UsernameKey', null, SyncType.local),
    /** @deprecated replaced by workspaces */
    selectedUdView: createSettings<string | null>('SelectedUdViewKey', null, SyncType.local),
    /** @deprecated replaced by workspaces */
    showAirportMonitorHome: createSettings<boolean>('ShowAirportsHomeScreenKey', false, SyncType.local),
    /** @deprecated replaced by workspaces */
    showFleetMonitorHome: createSettings<boolean>('ShowAircraftHomeScreenKey', false, SyncType.local),
    /** @deprecated replaced by workspaces */
    showEventListHome: createSettings<boolean>('ShowAlertsHomeScreenKey', false, SyncType.local),
    /** @deprecated replaced by workspaces */
    showMapLegendHome: createSettings<boolean>('ShowWeatherLegendScreenKey', false, SyncType.local),
    integrationSectionUnfolded: createSettings<boolean>('IntegrationSectionUnfoldedKey', false, SyncType.local),
    diversionAirlineTerminology: createSettings<DiversionManagerAirlineEnum>(
        'DiversionAirlineTerminologyKey',
        DiversionManagerAirlineEnum.airline,
        SyncType.local
    ),
    diversionNetworkTerminology: createSettings<DiversionManagerNetworkEnum>(
        'DiversionNetworkTerminologyKey',
        DiversionManagerNetworkEnum.network,
        SyncType.local
    ),
    diversionOtherTerminology: createSettings<DiversionManagerOtherEnum>(
        'DiversionOtherTerminologyKey',
        DiversionManagerOtherEnum.other,
        SyncType.local
    ),
    diversionCapacityFieldAirline: createSettings<DiversionManagerCapacityFieldEnum>(
        'DiversionCapacityFieldAirlineKey',
        DiversionManagerCapacityFieldEnum.wideNarrow,
        SyncType.local
    ),
    diversionCapacityFieldNetwork: createSettings<DiversionManagerCapacityFieldEnum>(
        'DiversionCapacityFieldNetworkKey',
        DiversionManagerCapacityFieldEnum.narrow,
        SyncType.local
    ),
    diversionCapacityFieldOther: createSettings<DiversionManagerCapacityFieldEnum>(
        'DiversionCapacityFieldOtherKey',
        DiversionManagerCapacityFieldEnum.narrow,
        SyncType.local
    ),
    diversionPriorityType: createSettings<DiversionPriorityTypeEnum>(
        'DiversionPriorityTypeKey',
        DiversionPriorityTypeEnum.faa,
        SyncType.local
    ),
    welcomeHelper: createSettings<WelcomeHelperSettings>('WelcomeHelperKey', DEFAULT_WELCOME_HELPER, SyncType.local),
    debugFlightIds: createSettings<number[]>('debugFlightIds', [], SyncType.local),
    enableMessageDebugger: createSettings<boolean>('enableMessageDebugger', false, SyncType.local),
    enableIntegrationMessageDebugger: createSettings<boolean>('enableIntegrationMessageDebugger', false, SyncType.local),
    autoLogin: createSettings<boolean>('autoLogin', false, SyncType.local),
    autoLoginPassword: createSettings<string | null>('autoLoginPassword', null, SyncType.local),
    monitorThresholdEditorAlertDetailedView: createSettings<boolean>('monitorThresholdEditorAlertDetailedView', false, SyncType.local),
    coastModeDebugOverlay: createSettings('coastModeDebugOverlay', false, SyncType.local),
    hideEmptyDashboardWidgets: createSettings<boolean>('hideEmptyDashboardWidgets', false, SyncType.local)
} as const satisfies Record<string, SettingsDefinition>;

function createSettings<T>(key: string, _default: T, sync: SyncType): SettingsDefinition<T> {
    return {
        key,
        default: _default,
        sync,
        defaultValueJson: JSON.stringify(_default)
    };
}

export const SETTINGS_DEFINITIONS_ENTRIES = Object.entries(SETTINGS_DEFINITIONS);
export type SettingValue<K extends keyof AppSettings> = (typeof SETTINGS_DEFINITIONS)[K]['default'];
export type SettingKeys = keyof typeof SETTINGS_DEFINITIONS;
export type PickSettingKeys<T> = {
    [K in SettingKeys]: (typeof SETTINGS_DEFINITIONS)[K] extends SettingsDefinition<T> ? K : never;
}[SettingKeys];

export type SettingBooleanKeys = PickSettingKeys<boolean>;
export type SettingsEnumLikeKeys = PickSettingKeys<number>;
export type SettingsStringKeys = PickSettingKeys<string | null>;

export type AppSettings = {
    [K in SettingKeys]: SettingValue<K>;
};

export function createDefaultSettings() {
    return Object.fromEntries(
        Object.keys(SETTINGS_DEFINITIONS).map((x) => [x, SETTINGS_DEFINITIONS[x as keyof typeof SETTINGS_DEFINITIONS]['default']])
    ) as AppSettings;
}

export function getDefaultSetting<K extends SettingKeys>(key: K): SettingValue<K> {
    return SETTINGS_DEFINITIONS[key].default;
}
